import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form as FinalForm, Field } from 'react-final-form';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import * as validators from '../../util/validators';

import {
  Page,
  Form,
  FieldTextInput,
  FieldSelect,
  PrimaryButton,
  ValidationError,
  IconSpinner,
  NamedLink,
  LayoutSingleColumn,
  FieldPhoneNumberInput,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './PrivateAccessPage.module.css';
import classNames from 'classnames';
import { submitPrivateAccessRequest } from '../../util/api';

// ---------------------------------------------------------------------------
// CustomSelect – panel-style dropdown matching the design
// ---------------------------------------------------------------------------
const CustomSelect = ({ input, meta, options, placeholder, label, id, maxHeight }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selectedOption = options.find(o => o.value === input.value);

  // Close on outside click
  useEffect(() => {
    const handleClick = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = value => {
    input.onChange(value);
    setOpen(false);
  };

  const hasError = meta.touched && meta.error;
  const panelStyle = maxHeight ? { maxHeight } : undefined;

  return (
    <div className={css.customSelectWrapper} ref={ref}>
      {label && (
        <label htmlFor={id} className={css.fieldLabel}>
          {label}
        </label>
      )}
      <button
        id={id}
        type="button"
        className={`${css.customSelectTrigger} ${open ? css.customSelectTriggerOpen : ''} ${hasError ? css.customSelectTriggerError : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selectedOption ? css.customSelectValue : css.customSelectPlaceholder}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg
          className={`${css.customSelectChevron} ${open ? css.customSelectChevronOpen : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M8 10L12 14L16 10"
            stroke="#888888"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <ul className={css.customSelectPanel} role="listbox" style={panelStyle}>
          {options.map(opt => (
            <li
              key={opt.value}
              role="option"
              aria-selected={input.value === opt.value}
              className={`${css.customSelectOption} ${input.value === opt.value ? css.customSelectOptionActive : ''}`}
              onClick={() => handleSelect(opt.value)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}

      {hasError && <span className={css.customSelectError}>{meta.error}</span>}
    </div>
  );
};

// ---------------------------------------------------------------------------

const PrivateAccessPageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();
  const intl = useIntl();
  const history = useHistory();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);

  const handleFormSubmit = async values => {
    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      await submitPrivateAccessRequest({
        fullName: values.fullName,
        email: values.email,
        phoneNumber: `${values.countryCode || ''}${values.phoneNumber || ''}`,
        interestedIn: values.interestedIn,
        preferredMonth: values.preferredMonth,
      });
      setIsSubmitting(false);
      setIsSubmitted(true);
    } catch (error) {
      setIsSubmitting(false);
      setSubmissionError(error.message || 'Unable to send your request. Please try again.');
    }
  };

  const handleReturn = () => {
    history.push('/');
  };

  // Validators
  const nameRequired = validators.required(
    intl.formatMessage({ id: 'PrivateAccessPage.requiredField' })
  );
  const emailRequired = validators.required(
    intl.formatMessage({ id: 'PrivateAccessPage.requiredField' })
  );
  const emailValid = validators.emailFormatValid(
    intl.formatMessage({ id: 'PrivateAccessPage.invalidEmail' })
  );
  const phoneRequired = validators.composeValidators(
    validators.required(
      intl.formatMessage({ id: 'PrivateAccessPage.requiredField' })
    ),
    validators.validPhoneNumber(
      intl.formatMessage({ id: 'PrivateAccessPage.phoneInvalid', defaultMessage: 'Please enter a valid phone number.' }),
      'countryCode'
    )
  );
  const interestedInRequired = validators.required(
    intl.formatMessage({ id: 'PrivateAccessPage.requiredField' })
  );
  const preferredMonthRequired = validators.required(
    intl.formatMessage({ id: 'PrivateAccessPage.requiredField' })
  );

  const initialValues = {
    countryCode: '+1',
  };

  const schemaTitle = intl.formatMessage(
    { id: 'PrivateAccessPage.schemaTitle' },
    { marketplaceName: config.marketplaceName }
  );

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        mainColumnClassName={css.fullWidthMain}
      >
        <div className={css.root}>
          {/* Centered card content container */}
          <div className={css.contentContainer}>
            {/* Header Title Section */}
            <div className={css.headerSection}>
              <h1 className={css.pageTitle}>
                <FormattedMessage id="PrivateAccessPage.title" />
              </h1>
              <p className={css.pageSubtitle}>
                <FormattedMessage id="PrivateAccessPage.subtitle" />
              </p>
            </div>

            {/* Main Form/Success Card */}
            <div className={css.card}>
              {isSubmitted ? (
                /* Success View */
                <div className={css.successView}>
                  <div className={css.successBadge}>
                    <FormattedMessage id="PrivateAccessPage.successBadge" />
                  </div>
                  <h2 className={css.successTitle}>
                    <FormattedMessage id="PrivateAccessPage.successTitle" />
                  </h2>
                  <p className={css.successSubtitle}>
                    <FormattedMessage id="PrivateAccessPage.successSubtitle" />
                  </p>
                  <div className={css.divider} />
                  {/* <p className={css.successConfirmation}>
                    <FormattedMessage id="PrivateAccessPage.successConfirmation" />
                  </p> */}
                  <button type="button" onClick={handleReturn} className={css.returnBtn}>
                    <FormattedMessage id="PrivateAccessPage.returnButtonText" />
                  </button>
                </div>
              ) : (
                /* Form View */
                <FinalForm
                  onSubmit={handleFormSubmit}
                  initialValues={initialValues}
                  render={formRenderProps => {
                    const { handleSubmit, invalid, pristine, values } = formRenderProps;

                    return (
                      <Form onSubmit={handleSubmit} className={css.form}>
                        <div className={css.cardContent}>
                          <h3 className={css.cardHeading}>
                            <FormattedMessage id="PrivateAccessPage.yourDetails" />
                          </h3>

                          {/* Full Name field */}
                          <div className={css.fieldWrapper}>
                            <label htmlFor="fullName" className={css.fieldLabel}>
                              <FormattedMessage id="PrivateAccessPage.fullNameLabel" />
                            </label>
                            <FieldTextInput
                              id="fullName"
                              name="fullName"
                              type="text"
                              placeholder={intl.formatMessage({
                                id: 'PrivateAccessPage.fullNamePlaceholder',
                              })}
                              validate={nameRequired}
                              rootClassName={css.fieldInputRoot}
                            />
                          </div>

                          {/* Email field */}
                          <div className={css.fieldWrapper}>
                            <label htmlFor="email" className={css.fieldLabel}>
                              <FormattedMessage id="PrivateAccessPage.emailLabel" />
                            </label>
                            <FieldTextInput
                              id="email"
                              name="email"
                              type="email"
                              placeholder={intl.formatMessage({
                                id: 'PrivateAccessPage.emailPlaceholder',
                              })}
                              validate={validators.composeValidators(emailRequired, emailValid)}
                              rootClassName={css.fieldInputRoot}
                            />
                          </div>

                          {/* Phone field */}
                          <div className={classNames(css.fieldWrapper, css.phInput)}>
                            <FieldPhoneNumberInput
                              id="phoneNumber"
                              name="phoneNumber"
                              countryCodeName="countryCode"
                              phoneNumberName="phoneNumber"
                              label={intl.formatMessage({ id: 'PrivateAccessPage.phoneLabel' })}
                              placeholder={intl.formatMessage({
                                id: 'PrivateAccessPage.phonePlaceholder',
                              })}
                              validate={phoneRequired}
                              invalidMessage={intl.formatMessage({ id: 'PrivateAccessPage.phoneInvalid', defaultMessage: 'Please enter a valid phone number.' })}
                              rootClassName={css.fieldInputRoot}
                              labelClassName={css.fieldLabel}
                              inputClassName={classNames('phoneInputGroup', css.phoneInputGroup)}
                              useScrollableCountrySelect
                              countrySelectMaxHeight={320}
                            />
                          </div>

                          {/* Dropdowns Row */}
                          <div className={css.dropdownsRow}>
                            {/* Interested In */}
                            <Field
                              name="interestedIn"
                              validate={interestedInRequired}
                              render={fieldProps => (
                                <CustomSelect
                                  {...fieldProps}
                                  id="interestedIn"
                                  label={intl.formatMessage({ id: 'PrivateAccessPage.interestedInLabel' })}
                                  placeholder={intl.formatMessage({ id: 'PrivateAccessPage.interestedInPlaceholder' })}
                                  options={[
                                    { value: 'Architecture', label: 'Architecture' },
                                    { value: 'Culinary', label: 'Culinary' },
                                    { value: 'Culture', label: 'Culture' },
                                    { value: 'Happiness', label: 'Happiness' },
                                    { value: 'Heritage', label: 'Heritage' },
                                    { value: 'History', label: 'History' },
                                    { value: 'Nature', label: 'Nature' },
                                    { value: 'Wellness', label: 'Wellness' },
                                    { value: 'Bespoke Journey', label: 'Bespoke Journey' },
                                  ]}
                                />
                              )}
                            />

                            {/* Preferred Month */}
                            <Field
                              name="preferredMonth"
                              validate={preferredMonthRequired}
                              render={fieldProps => (
                                <CustomSelect
                                  {...fieldProps}
                                  id="preferredMonth"
                                  label={intl.formatMessage({ id: 'PrivateAccessPage.preferredMonthLabel' })}
                                  placeholder={intl.formatMessage({ id: 'PrivateAccessPage.preferredMonthPlaceholder' })}
                                  maxHeight={240}
                                  options={[
                                    { value: 'January', label: 'January' },
                                    { value: 'February', label: 'February' },
                                    { value: 'March', label: 'March' },
                                    { value: 'April', label: 'April' },
                                    { value: 'May', label: 'May' },
                                    { value: 'June', label: 'June' },
                                    { value: 'July', label: 'July' },
                                    { value: 'August', label: 'August' },
                                    { value: 'September', label: 'September' },
                                    { value: 'October', label: 'October' },
                                    { value: 'November', label: 'November' },
                                    { value: 'December', label: 'December' },
                                  ]}
                                />
                              )}
                            />
                          </div>
                        </div>

                        {/* Submit Button */}
                        <button
                          type="submit"
                          className={css.submitBtn}
                          disabled={isSubmitting || invalid}
                        >
                          {isSubmitting ? (
                            <IconSpinner className={css.spinner} />
                          ) : (
                            <FormattedMessage id="PrivateAccessPage.submitButtonText" />
                          )}
                        </button>
                        {submissionError ? (
                          <p className={css.submissionError}>{submissionError}</p>
                        ) : null}
                      </Form>
                    );
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

PrivateAccessPageComponent.propTypes = {
  scrollingDisabled: PropTypes.bool.isRequired,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const PrivateAccessPage = compose(
  connect(
    mapStateToProps,
    null
  )
)(PrivateAccessPageComponent);

export default PrivateAccessPage;
